/**
 * SupabaseBookmarkAgent
 *
 * Agent for managing bookmark CRUD operations with Supabase.
 * Implements 10 skills for database-backed bookmark management.
 *
 * Skills:
 * - ADD_BOOKMARK: Add a flashcard to bookmarks
 * - REMOVE_BOOKMARK: Remove a flashcard from bookmarks
 * - TOGGLE_BOOKMARK: Toggle bookmark state
 * - GET_BOOKMARKS: Get all bookmarked flashcard IDs
 * - SYNC_BOOKMARKS: Sync multiple bookmarks to database
 * - CLEAR_ALL_BOOKMARKS: Remove all bookmarks
 * - IS_BOOKMARKED: Check if a flashcard is bookmarked
 * - GET_BOOKMARKED_FLASHCARDS: Filter flashcards to bookmarked ones
 * - SET_BOOKMARKS: Replace all bookmarks with new set
 * - GET_STATE: Get current agent state
 */

import { supabase, isSupabaseConfigured } from '../../../config/supabase';

class SupabaseBookmarkAgent {
  constructor() {
    this._auth0Id = null;
    this._bookmarkedIds = [];
    this._isLoading = false;
    this._lastError = null;
  }

  /**
   * Set the Auth0 user ID for database operations
   * @param {string|null} auth0Id - The Auth0 user ID (user.sub)
   */
  setAuth0Id(auth0Id) {
    this._auth0Id = auth0Id;
    if (!auth0Id) {
      this._bookmarkedIds = [];
    }
  }

  /**
   * Execute a skill
   * @param {string} skill - The skill to execute
   * @param {...any} args - Arguments for the skill
   * @returns {Promise<any>|any} - Result of the skill execution
   */
  execute(skill, ...args) {
    switch (skill) {
      case 'ADD_BOOKMARK':
        return this._addBookmark(args[0]);
      case 'REMOVE_BOOKMARK':
        return this._removeBookmark(args[0]);
      case 'TOGGLE_BOOKMARK':
        return this._toggleBookmark(args[0]);
      case 'GET_BOOKMARKS':
        return this._getBookmarks();
      case 'SYNC_BOOKMARKS':
        return this._syncBookmarks(args[0]);
      case 'CLEAR_ALL_BOOKMARKS':
        return this._clearAllBookmarks();
      case 'IS_BOOKMARKED':
        return this._isBookmarked(args[0]);
      case 'GET_BOOKMARKED_FLASHCARDS':
        return this._getBookmarkedFlashcards(args[0]);
      case 'SET_BOOKMARKS':
        return this._setBookmarks(args[0]);
      case 'GET_STATE':
        return this._getState();
      default:
        console.warn(`[SupabaseBookmarkAgent] Unknown skill: ${skill}`);
        return { success: false, error: `Unknown skill: ${skill}` };
    }
  }

  /**
   * Add a bookmark
   * @private
   */
  async _addBookmark(flashcardId) {
    if (!this._auth0Id || !isSupabaseConfigured) {
      return { success: false, error: 'Not authenticated or Supabase not configured' };
    }

    try {
      this._isLoading = true;

      const { error } = await supabase
        .from('bookmarks')
        .upsert({
          auth0_id: this._auth0Id,
          flashcard_id: flashcardId
        }, {
          onConflict: 'auth0_id,flashcard_id'
        });

      if (error) throw error;

      if (!this._bookmarkedIds.includes(flashcardId)) {
        this._bookmarkedIds.push(flashcardId);
      }

      this._isLoading = false;
      return {
        success: true,
        bookmarkedIds: [...this._bookmarkedIds],
        count: this._bookmarkedIds.length
      };
    } catch (error) {
      console.error('[SupabaseBookmarkAgent] Add bookmark error:', error);
      this._lastError = error.message;
      this._isLoading = false;
      return { success: false, error: error.message };
    }
  }

  /**
   * Remove a bookmark
   * @private
   */
  async _removeBookmark(flashcardId) {
    if (!this._auth0Id || !isSupabaseConfigured) {
      return { success: false, error: 'Not authenticated or Supabase not configured' };
    }

    try {
      this._isLoading = true;

      const { error } = await supabase
        .from('bookmarks')
        .delete()
        .eq('auth0_id', this._auth0Id)
        .eq('flashcard_id', flashcardId);

      if (error) throw error;

      this._bookmarkedIds = this._bookmarkedIds.filter(id => id !== flashcardId);

      this._isLoading = false;
      return {
        success: true,
        bookmarkedIds: [...this._bookmarkedIds],
        count: this._bookmarkedIds.length
      };
    } catch (error) {
      console.error('[SupabaseBookmarkAgent] Remove bookmark error:', error);
      this._lastError = error.message;
      this._isLoading = false;
      return { success: false, error: error.message };
    }
  }

  /**
   * Toggle a bookmark
   * @private
   */
  async _toggleBookmark(flashcardId) {
    const isCurrentlyBookmarked = this._bookmarkedIds.includes(flashcardId);

    if (isCurrentlyBookmarked) {
      const result = await this._removeBookmark(flashcardId);
      return { ...result, isBookmarked: false };
    } else {
      const result = await this._addBookmark(flashcardId);
      return { ...result, isBookmarked: true };
    }
  }

  /**
   * Get all bookmarks from database
   * @private
   */
  async _getBookmarks() {
    if (!this._auth0Id || !isSupabaseConfigured) {
      return { success: false, bookmarkedIds: [], count: 0 };
    }

    try {
      this._isLoading = true;

      const { data, error } = await supabase
        .from('bookmarks')
        .select('flashcard_id')
        .eq('auth0_id', this._auth0Id)
        .order('created_at', { ascending: true });

      if (error) throw error;

      this._bookmarkedIds = data.map(row => row.flashcard_id);
      this._isLoading = false;

      return {
        success: true,
        bookmarkedIds: [...this._bookmarkedIds],
        count: this._bookmarkedIds.length
      };
    } catch (error) {
      console.error('[SupabaseBookmarkAgent] Get bookmarks error:', error);
      this._lastError = error.message;
      this._isLoading = false;
      return { success: false, bookmarkedIds: [], count: 0, error: error.message };
    }
  }

  /**
   * Sync bookmarks to database (for migration)
   * @private
   */
  async _syncBookmarks(bookmarkIds) {
    if (!this._auth0Id || !isSupabaseConfigured || !Array.isArray(bookmarkIds)) {
      return { success: false, syncedCount: 0 };
    }

    try {
      this._isLoading = true;

      const records = bookmarkIds.map(flashcardId => ({
        auth0_id: this._auth0Id,
        flashcard_id: flashcardId
      }));

      const { error } = await supabase
        .from('bookmarks')
        .upsert(records, {
          onConflict: 'auth0_id,flashcard_id'
        });

      if (error) throw error;

      // Refresh local state
      await this._getBookmarks();

      this._isLoading = false;
      return {
        success: true,
        syncedCount: bookmarkIds.length
      };
    } catch (error) {
      console.error('[SupabaseBookmarkAgent] Sync bookmarks error:', error);
      this._lastError = error.message;
      this._isLoading = false;
      return { success: false, syncedCount: 0, error: error.message };
    }
  }

  /**
   * Clear all bookmarks
   * @private
   */
  async _clearAllBookmarks() {
    if (!this._auth0Id || !isSupabaseConfigured) {
      return { success: false, clearedCount: 0 };
    }

    try {
      this._isLoading = true;
      const previousCount = this._bookmarkedIds.length;

      const { error } = await supabase
        .from('bookmarks')
        .delete()
        .eq('auth0_id', this._auth0Id);

      if (error) throw error;

      this._bookmarkedIds = [];
      this._isLoading = false;

      return {
        success: true,
        clearedCount: previousCount
      };
    } catch (error) {
      console.error('[SupabaseBookmarkAgent] Clear all bookmarks error:', error);
      this._lastError = error.message;
      this._isLoading = false;
      return { success: false, clearedCount: 0, error: error.message };
    }
  }

  /**
   * Check if a flashcard is bookmarked
   * @private
   */
  _isBookmarked(flashcardId) {
    return {
      isBookmarked: this._bookmarkedIds.includes(flashcardId)
    };
  }

  /**
   * Get bookmarked flashcards from array
   * @private
   */
  _getBookmarkedFlashcards(flashcards) {
    if (!Array.isArray(flashcards)) {
      return { flashcards: [], count: 0 };
    }

    const bookmarkedSet = new Set(this._bookmarkedIds);
    const filtered = flashcards.filter(card => {
      const cardId = card.id || card.flashcardId || card._id;
      return bookmarkedSet.has(cardId);
    });

    return {
      flashcards: filtered,
      count: filtered.length
    };
  }

  /**
   * Set bookmarks (replace all)
   * @private
   */
  async _setBookmarks(bookmarkIds) {
    if (!this._auth0Id || !isSupabaseConfigured) {
      return { success: false, count: 0 };
    }

    try {
      this._isLoading = true;

      // Clear existing and add new
      await this._clearAllBookmarks();
      await this._syncBookmarks(bookmarkIds);

      this._isLoading = false;
      return {
        success: true,
        count: this._bookmarkedIds.length
      };
    } catch (error) {
      console.error('[SupabaseBookmarkAgent] Set bookmarks error:', error);
      this._lastError = error.message;
      this._isLoading = false;
      return { success: false, count: 0, error: error.message };
    }
  }

  /**
   * Get current state
   * @private
   */
  _getState() {
    return {
      bookmarkedIds: [...this._bookmarkedIds],
      count: this._bookmarkedIds.length,
      isLoading: this._isLoading,
      lastError: this._lastError,
      isAuthenticated: !!this._auth0Id
    };
  }
}

// Export singleton instance
const instance = new SupabaseBookmarkAgent();
export default instance;
export { SupabaseBookmarkAgent };
